import { useMemo } from "react";
import { Tooltip } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { LuClipboardEdit } from "react-icons/lu";
import { BiCommentError } from "react-icons/bi";
import { PiWarningFill } from "react-icons/pi";
import { GiSandsOfTime } from "react-icons/gi";

import { archiveReasons } from "../common/dictionaries/archiveReasons";
import { failedReasons } from "../common/dictionaries/failedReasons";
import { parseInteractionHistory } from "@/utils/orderTransformers";
import type { OrderStatus, StatusConfig, InteractionHistoryEntry, InteractionHistory } from "./types";

interface OrderStatusBadgeProps {
  status: OrderStatus;
  interactionHistory?: string | InteractionHistory;
}

const STATUS_CONFIG: Record<OrderStatus, Omit<StatusConfig, "text">> = {
  pending_review: {
    classes: "bg-gradient-to-r from-vistactaorangelight to-vistactaorange text-white",
    icon: "LuClipboardEdit",
  },
  success: {
    classes: "bg-gradient-to-r from-vistactagreenlight to-vistactagreen text-white",
    icon: "LuClipboardEdit",
  },
  archive: {
    classes: "bg-white",
    icon: "BiCommentError",
  },
  failed: {
    classes: "bg-gradient-to-r from-red-700 to-red-500 text-white",
    icon: "PiWarningFill",
  },
  active: {
    classes: "bg-gradient-to-r from-violet-700 to-violet-500 text-white",
    icon: "GiSandsOfTime",
  },
};

function StatusIcon({ icon }: Readonly<{ icon: StatusConfig["icon"] }>) {
  switch (icon) {
    case "LuClipboardEdit":
      return <LuClipboardEdit className="ml-2" />;
    case "BiCommentError":
      return <BiCommentError className="ml-2 text-2xl" />;
    case "PiWarningFill":
      return <PiWarningFill className="ml-2" />;
    case "GiSandsOfTime":
      return <GiSandsOfTime className="ml-2" />;
    default:
      return null;
  }
};

export function OrderStatusBadge({
  status,
  interactionHistory,
}: Readonly<OrderStatusBadgeProps>) {
  const { t } = useTranslation();

  const statusConfig = useMemo((): StatusConfig => {
    const config = STATUS_CONFIG[status] || { classes: "", icon: "" as const };
    let text = "";

    switch (status) {
      case "pending_review":
        text = `${t("to_process")} |`;
        break;
      case "success":
        text = `${t("validated")} |`;
        break;
      case "archive":
        text = t("archived");
        break;
      case "failed":
        text = `${t("error")} |`;
        break;
      case "active":
        text = `${t("processing")} |`;
        break;
    }

    return { ...config, text };
  }, [status, t]);

  const badgeContent = (
    <div
      className={`rounded-full text-center text-small h-7 items-center leading-7 flex justify-center font-bold w-32 ${statusConfig.classes}`}
    >
      <span className="text-xs">{statusConfig.text}</span>
      <StatusIcon icon={statusConfig.icon} />
    </div>
  );

  if (["archive", "failed"].includes(status) && interactionHistory) {
    const parsedHistory = parseInteractionHistory(interactionHistory);

    const historySegment =
      status === "failed" ? parsedHistory.failed : parsedHistory.archive;

    let latestEntry: InteractionHistoryEntry | null = null;

    if (historySegment) {
      Object.values(historySegment).forEach((entryArray) => {
        if (Array.isArray(entryArray)) {
          entryArray.forEach((historyItem: InteractionHistoryEntry) => {
            if (
              !latestEntry ||
              new Date(historyItem.datetime) > new Date(latestEntry.datetime)
            ) {
              latestEntry = historyItem;
            }
          });
        }
      });
    }

    if (latestEntry) {
      const dictionary = status === "archive" ? archiveReasons : failedReasons;
      const itemLabel = dictionary.find(
        (item: { value: string; label: string }) => item.value === (latestEntry as InteractionHistoryEntry).reason
      );
      const reason = itemLabel ? t(itemLabel.label) : t("not_specified");
      const comment = (latestEntry as InteractionHistoryEntry).comment || t("not_specified");

      return (
        <Tooltip
          content={
            <div className="px-1 py-2">
              <div className="text-small font-bold">{reason}</div>
              <div className="text-tiny">{comment}</div>
            </div>
          }
        >
          {badgeContent}
        </Tooltip>
      );
    }
  }

  return badgeContent;
};

export default OrderStatusBadge;
